/**
 * RecurringModals — Knit's "Add Recurring Income" and "Add Recurring
 * Deduction" modals (exact field sets + option lists).
 */
import { useState } from "react";
import { X } from "lucide-react";
import { RECURRING_INCOME_TYPES, RECURRING_DEDUCTION_TYPES, HOURS_BASED_INCOMES } from "./knitData";

const L = ({ children }) => (
  <span className="mb-0.5 block font-mono text-[9px] uppercase tracking-widest text-white/40">{children}</span>
);
const inputCls = "h-8 w-full rounded-md border border-white/10 bg-white/[0.03] px-2 text-sm text-white/90 focus:border-[#1a3fd1] focus:outline-none";
const selCls = `${inputCls}`;

const Shell = ({ title, onClose, onSave, busy, saveTestid, children }) => (
  <div className="fixed inset-0 z-[160] flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-black/70" onClick={onClose} />
    <div className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl border border-white/10 bg-[#101014] p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-bold text-white">{title}</h3>
        <button onClick={onClose} aria-label="Close" className="rounded p-1 text-white/50 hover:bg-white/10 hover:text-white"><X className="h-4 w-4" /></button>
      </div>
      <div className="space-y-3">{children}</div>
      <div className="mt-4 flex gap-2">
        <button onClick={onSave} disabled={busy} data-testid={saveTestid}
          className="rounded-md bg-amber-600 px-5 py-2 font-mono text-[10px] font-bold uppercase tracking-widest text-white hover:bg-amber-500 disabled:opacity-50">
          {busy ? "Saving…" : "Save"}
        </button>
        <button onClick={onClose} className="rounded-md border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-white/70 hover:text-white">
          Cancel
        </button>
      </div>
    </div>
  </div>
);

export const AddRecurringIncomeModal = ({ onClose, onSave }) => {
  const [f, setF] = useState({ type: "", pay_rate: "", amount: "" });
  const [busy, setBusy] = useState(false);
  const hoursBased = HOURS_BASED_INCOMES.includes(f.type);
  const save = async () => {
    if (!f.type) return;
    setBusy(true);
    await onSave({ type: f.type, pay_rate: f.pay_rate || "Wage 1", amount: parseFloat(f.amount) || 0 });
    setBusy(false);
  };
  return (
    <Shell title="Add Recurring Income" onClose={onClose} onSave={save} busy={busy} saveTestid="add-income-save">
      <label className="block"><L>*Additional Income</L>
        <select value={f.type} onChange={(e) => setF((p) => ({ ...p, type: e.target.value }))} data-testid="add-income-type" className={selCls}>
          <option value="">Please select</option>
          {RECURRING_INCOME_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select></label>
      <label className="block"><L>Applied to Pay Rate</L>
        <select value={f.pay_rate} onChange={(e) => setF((p) => ({ ...p, pay_rate: e.target.value }))} data-testid="add-income-payrate" className={selCls}>
          <option value="">Please select</option>
          <option value="Wage 1">Wage 1</option>
        </select></label>
      <label className="block"><L>Default Amount Per Pay Period {hoursBased ? "(hours)" : "($)"}</L>
        <input type="number" step="0.01" value={f.amount} data-testid="add-income-amount"
          onChange={(e) => setF((p) => ({ ...p, amount: e.target.value }))} className={inputCls} /></label>
      <p className="text-[11px] font-semibold text-red-400">
        New recurring incomes will not be applied until after the current payroll has concluded
      </p>
    </Shell>
  );
};

export const AddRecurringDeductionModal = ({ onClose, onSave }) => {
  const [f, setF] = useState({ type: "", pct: "", per_hour: "", annual_max: "", period_max: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.type) return;
    setBusy(true);
    await onSave({
      type: f.type, option: "Amount",
      pct: parseFloat(f.pct) || 0, per_hour: parseFloat(f.per_hour) || 0,
      annual_max: parseFloat(f.annual_max) || 0, period_max: parseFloat(f.period_max) || 0,
    });
    setBusy(false);
  };
  return (
    <Shell title="Add Recurring Deduction" onClose={onClose} onSave={save} busy={busy} saveTestid="add-deduction-save">
      <label className="block"><L>*Deduction Type</L>
        <select value={f.type} onChange={(e) => setF((p) => ({ ...p, type: e.target.value }))} data-testid="add-deduction-type" className={selCls}>
          <option value="">Please select</option>
          {RECURRING_DEDUCTION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select></label>
      <label className="block"><L>Deduction Option</L>
        <input value="Amount" readOnly className={`${inputCls} cursor-default text-white/40`} /></label>
      <label className="block"><L>Default Amount Per Pay Period (%)</L>
        <input type="number" step="0.01" value={f.pct} data-testid="add-deduction-pct"
          onChange={(e) => setF((p) => ({ ...p, pct: e.target.value }))} className={inputCls} /></label>
      <label className="block"><L>Default Amount Per Hour ($)</L>
        <input type="number" step="0.01" value={f.per_hour} data-testid="add-deduction-per-hour"
          onChange={(e) => setF((p) => ({ ...p, per_hour: e.target.value }))} className={inputCls} /></label>
      <label className="block"><L>Annual Maximum ($)</L>
        <input type="number" step="0.01" value={f.annual_max} data-testid="add-deduction-annual-max"
          onChange={(e) => setF((p) => ({ ...p, annual_max: e.target.value }))} className={inputCls} /></label>
      <label className="block"><L>Pay Period Maximum ($)</L>
        <input type="number" step="0.01" value={f.period_max} data-testid="add-deduction-period-max"
          onChange={(e) => setF((p) => ({ ...p, period_max: e.target.value }))} className={inputCls} /></label>
    </Shell>
  );
};
